import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import type {
  PlayEvent,
  PlayEventMatch,
  PlayEventParticipant,
  PlayEventRound,
  PlayEventTeam,
  PlayerRatingSettings,
} from '@court-sync/shared';
import {
  flattenDrawPreviewMatches,
  getPlayEventMatchSideParticipants,
  getPlayEventParticipantDisplayName,
  getPlayEventRoundDrawStatusLabel,
  getSittingOutParticipantsForRound,
  isPlayEventRoundEditable,
  sortPlayEventMatchesForDisplay,
  getSingleEliminationGlobalMatchIndex,
  getSingleEliminationMatchDisplay,
} from '@court-sync/shared';
import { getAvailableParticipantsForLiveRound } from '@/lib/playEventDrawUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Badge,
  Button,
} from '@/components/ui';
import {
  PlayEventDrawPlayerDragGhost,
} from '@/components/play/PlayEventDrawPlayerSlot';
import {
  PlayEventDrawMatchList,
  playEventDrawMatchListDragHandler,
  type DrawMatchListItem,
} from '@/components/play/PlayEventDrawMatchList';
import { PlayEventBracketTree } from '@/components/play/PlayEventBracketTree';
import { playerRatingService } from '@/services/playerRating';
import { playEventService } from '@/services/playEvent';
import { clubService } from '@/services/club';
import { snapCenterToCursor } from '@/lib/playEventDrawDnd';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';

interface PlayEventDrawDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  event: PlayEvent;
  rounds: PlayEventRound[];
  matches: PlayEventMatch[];
  participants: PlayEventParticipant[];
  teams: PlayEventTeam[];
  courtNamesById: Record<string, string>;
  initialRoundNumber?: number | null;
  rosterParticipants?: PlayEventParticipant[];
  participantsLoading?: boolean;
  onParticipantUpdated?: () => void;
  onMatchCourtUpdated?: (matchId: string, courtId: string | null) => void;
}

type DrawViewTab = 'round' | 'bracket';

function parseEventDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

export function PlayEventDrawDialog({
  open,
  onOpenChange,
  organizationId,
  event,
  rounds,
  matches,
  participants,
  teams,
  courtNamesById,
  initialRoundNumber = null,
  rosterParticipants,
  participantsLoading = false,
  onParticipantUpdated,
  onMatchCourtUpdated,
}: PlayEventDrawDialogProps) {
  const [ratingSettings, setRatingSettings] = useState<PlayerRatingSettings | null>(null);
  const [selectedRoundNumber, setSelectedRoundNumber] = useState<number | null>(initialRoundNumber);
  const [viewTab, setViewTab] = useState<DrawViewTab>('round');
  const [swapping, setSwapping] = useState(false);
  const [reallocating, setReallocating] = useState(false);
  const [assigningCourtMatchId, setAssigningCourtMatchId] = useState<string | null>(null);
  const [scoringMatchId, setScoringMatchId] = useState<string | null>(null);
  const [draggingParticipantId, setDraggingParticipantId] = useState<string | null>(null);
  const [loadedCourtNamesById, setLoadedCourtNamesById] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !event.clubId) {
      return undefined;
    }
    let cancelled = false;
    void clubService.getClubCourts(event.clubId).then((res) => {
      if (cancelled || !res.success || !res.data) return;
      setLoadedCourtNamesById(Object.fromEntries(res.data.map((court) => [court.id, court.name])));
    });
    return () => {
      cancelled = true;
    };
  }, [open, event.clubId]);

  const resolvedCourtNamesById = useMemo(
    () => ({ ...courtNamesById, ...loadedCourtNamesById }),
    [courtNamesById, loadedCourtNamesById]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  useEffect(() => {
    if (!open) return;
    void playerRatingService.getSettings(organizationId).then((res) => {
      if (res.success && res.data) {
        setRatingSettings(res.data);
      }
    });
  }, [open, organizationId]);

  const roundNumbers = useMemo(() => {
    const numbers = new Set<number>();
    for (const round of rounds) numbers.add(round.roundNumber);
    for (const match of matches) numbers.add(match.roundNumber);
    return [...numbers].sort((a, b) => a - b);
  }, [matches, rounds]);

  useEffect(() => {
    if (!open) {
      setSelectedRoundNumber(null);
      setViewTab('round');
      return;
    }
    setSelectedRoundNumber((current) => {
      if (current != null && roundNumbers.includes(current)) {
        return current;
      }
      if (initialRoundNumber != null && roundNumbers.includes(initialRoundNumber)) {
        return initialRoundNumber;
      }
      return roundNumbers[0] ?? null;
    });
  }, [open, initialRoundNumber, roundNumbers]);

  const participantsById = useMemo(() => {
    const map = new Map<string, PlayEventParticipant>();
    for (const participant of participants) {
      map.set(participant.id, participant);
    }
    return map;
  }, [participants]);

  const teamsById = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);

  const roundsByNumber = useMemo(
    () => new Map(rounds.map((round) => [round.roundNumber, round])),
    [rounds]
  );

  const sittingOutRoster =
    rosterParticipants ?? participants.filter((participant) => participant.status === 'checked_in');

  const selectedRound = selectedRoundNumber != null ? roundsByNumber.get(selectedRoundNumber) : undefined;
  const selectedRoundMatches = useMemo(() => {
    if (selectedRoundNumber == null) return [];
    return sortPlayEventMatchesForDisplay(
      matches.filter((match) => match.roundNumber === selectedRoundNumber)
    );
  }, [matches, selectedRoundNumber]);

  const roundEditable =
    selectedRoundNumber != null
      ? isPlayEventRoundEditable(selectedRound, matches, selectedRoundNumber, event)
      : false;

  const sittingOut =
    selectedRoundNumber != null && event.format === 'social_rotate'
      ? getSittingOutParticipantsForRound(
          selectedRoundNumber,
          sittingOutRoster,
          matches,
          teamsById
        )
      : [];

  const availableParticipantsForRound = useMemo(() => {
    if (selectedRoundNumber == null) return [];
    return getAvailableParticipantsForLiveRound(
      sittingOutRoster,
      matches,
      selectedRoundNumber,
      sittingOut.map((participant) => participant.id)
    );
  }, [matches, selectedRoundNumber, sittingOut, sittingOutRoster]);

  const availableParticipantOptions = useMemo(
    () =>
      availableParticipantsForRound.map((participant) => ({
        id: participant.id,
        displayName: getPlayEventParticipantDisplayName(participant),
      })),
    [availableParticipantsForRound]
  );

  const participantNamesById = useMemo(
    () =>
      Object.fromEntries(
        participants.map((participant) => [
          participant.id,
          getPlayEventParticipantDisplayName(participant),
        ])
      ),
    [participants]
  );

  const matchListItems: DrawMatchListItem[] = useMemo(() => {
    if (selectedRoundNumber == null) return [];
    const elimRoundCount = roundNumbers.length;

    return selectedRoundMatches.map((match, indexInRound) => {
      const elimDisplay =
        event.format === 'single_elimination'
          ? getSingleEliminationMatchDisplay(
              selectedRoundNumber,
              getSingleEliminationGlobalMatchIndex(
                selectedRoundNumber,
                indexInRound,
                elimRoundCount
              ),
              indexInRound,
              elimRoundCount,
              {
                sideAId: match.sideAParticipantId,
                sideBId: match.sideBParticipantId,
                isBye: match.result === 'bye',
              },
              participantNamesById
            )
          : null;

      const { sideA, sideB } = getPlayEventMatchSideParticipants(
        match,
        participantsById,
        teamsById
      );

      return {
        id: match.id,
        roundNumber: match.roundNumber,
        matchIndex: indexInRound,
        sideAIds: sideA.map((participant) => participant.id),
        sideBIds: sideB.map((participant) => participant.id),
        sideATeamId: match.sideATeamId,
        sideBTeamId: match.sideBTeamId,
        courtId: match.courtId,
        result: match.result,
        scoreLabel: match.score?.sets?.length
          ? match.score.sets.map((set) => `${set.a}-${set.b}`).join(', ')
          : undefined,
        winnerSide: match.winnerSide ?? null,
        score: match.score ?? null,
        isBye: match.result === 'bye',
        matchLabel: elimDisplay?.matchLabel,
        sideAPlaceholder: elimDisplay?.sideAPlaceholder,
        sideBPlaceholder: elimDisplay?.sideBPlaceholder,
        sideAIsFeeder: elimDisplay?.sideAIsFeeder,
        sideBIsFeeder: elimDisplay?.sideBIsFeeder,
      };
    });
  }, [
    event.format,
    participantNamesById,
    participantsById,
    roundNumbers.length,
    selectedRoundMatches,
    selectedRoundNumber,
    teamsById,
  ]);

  const bracketPreviewMatches = useMemo(
    () =>
      flattenDrawPreviewMatches({
        rounds: roundNumbers.map((roundNumber) => ({
          roundNumber,
          matches: sortPlayEventMatchesForDisplay(
            matches.filter((match) => match.roundNumber === roundNumber)
          ).map((match, matchIndex) => ({
            roundNumber,
            matchIndex,
            sideAId: match.sideAParticipantId,
            sideBId: match.sideBParticipantId,
            isBye: match.result === 'bye',
          })),
          sittingOut: [],
        })),
        totalMatches: matches.length,
        byeCount: matches.filter((match) => match.result === 'bye').length,
        unassignedParticipantIds: [],
      }),
    [matches, roundNumbers]
  );

  const draggingParticipant =
    draggingParticipantId != null ? participantsById.get(draggingParticipantId) ?? null : null;

  const handleDragStart = (dragEvent: DragStartEvent) => {
    setDraggingParticipantId(String(dragEvent.active.id));
  };

  const handleDragCancel = () => {
    setDraggingParticipantId(null);
  };

  const handleDragEnd = async (dragEvent: DragEndEvent) => {
    setDraggingParticipantId(null);

    if (!roundEditable || selectedRoundNumber == null || swapping) return;

    playEventDrawMatchListDragHandler(dragEvent, {
      onSwap: (sourceParticipantId, targetParticipantId) => {
        void (async () => {
          setSwapping(true);
          try {
            const res = await playEventService.swapRoundParticipants(
              organizationId,
              event.id,
              selectedRoundNumber,
              { sourceParticipantId, targetParticipantId }
            );
            if (res.success) {
              toast.success('Players swapped');
              onParticipantUpdated?.();
            } else {
              toast.error(res.error ?? 'Failed to swap players');
            }
          } finally {
            setSwapping(false);
          }
        })();
      },
      onAssignEmpty: (sourceParticipantId, matchKey, side) => {
        void (async () => {
          setSwapping(true);
          try {
            const res = await playEventService.assignRoundParticipant(
              organizationId,
              event.id,
              selectedRoundNumber,
              {
                participantId: sourceParticipantId,
                targetMatchId: matchKey,
                targetSide: side,
              }
            );
            if (res.success) {
              toast.success('Player assigned');
              onParticipantUpdated?.();
            } else {
              toast.error(res.error ?? 'Failed to assign player');
            }
          } finally {
            setSwapping(false);
          }
        })();
      },
    });
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!roundEditable || selectedRoundNumber == null || swapping) return;
    setSwapping(true);
    try {
      const res = await playEventService.removeRoundParticipant(
        organizationId,
        event.id,
        selectedRoundNumber,
        { participantId }
      );
      if (res.success) {
        toast.success('Player removed from round');
        onParticipantUpdated?.();
      } else {
        toast.error(res.error ?? 'Failed to remove player');
      }
    } finally {
      setSwapping(false);
    }
  };

  const handleCourtChange = async (matchId: string, courtId: string | null) => {
    if (!roundEditable || assigningCourtMatchId) return;
    const match = matches.find((entry) => entry.id === matchId);
    const previousCourtId = match?.courtId ?? null;
    setAssigningCourtMatchId(matchId);
    onMatchCourtUpdated?.(matchId, courtId);
    try {
      const res = await playEventService.assignMatchCourt(
        organizationId,
        event.id,
        matchId,
        courtId
      );
      if (res.success) {
        toast.success(courtId ? 'Court assigned' : 'Court cleared');
      } else {
        onMatchCourtUpdated?.(matchId, previousCourtId);
        toast.error(res.error ?? 'Failed to assign court');
      }
    } catch {
      onMatchCourtUpdated?.(matchId, previousCourtId);
      toast.error('Failed to assign court');
    } finally {
      setAssigningCourtMatchId(null);
    }
  };

  const handleRecordScore = async (
    matchId: string,
    data: Parameters<typeof playEventService.recordScore>[3]
  ) => {
    setScoringMatchId(matchId);
    try {
      const res = await playEventService.recordScore(organizationId, event.id, matchId, data);
      if (res.success) {
        toast.success('Score saved');
        onParticipantUpdated?.();
      } else {
        toast.error(res.error ?? 'Failed to record score');
      }
    } finally {
      setScoringMatchId(null);
    }
  };

  const handleReallocateRound = async () => {
    if (!roundEditable || selectedRoundNumber == null || event.format !== 'social_rotate') return;
    setReallocating(true);
    try {
      const res = await playEventService.reallocateRound(
        organizationId,
        event.id,
        selectedRoundNumber
      );
      if (res.success) {
        toast.success('Round reallocated');
        onParticipantUpdated?.();
      } else {
        toast.error(res.error ?? 'Failed to reallocate round');
      }
    } finally {
      setReallocating(false);
    }
  };

  const roundContent = (
    <div className="space-y-4">
      {selectedRound ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
          <div>
            <h3 className="text-base font-semibold text-foreground">Round {selectedRound.roundNumber}</h3>
            {selectedRound.startsAt ? (
              <p className="text-sm text-muted-foreground">
                {format(parseEventDate(selectedRound.startsAt), 'EEEE d MMM · HH:mm')}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {roundEditable && event.format === 'social_rotate' ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void handleReallocateRound()}
                isLoading={reallocating}
              >
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
                Re-run auto
              </Button>
            ) : null}
            <Badge variant={roundEditable ? 'secondary' : 'outline'}>
              {selectedRoundNumber != null
                ? getPlayEventRoundDrawStatusLabel(selectedRound, matches, selectedRoundNumber, event)
                : 'Scheduled'}
            </Badge>
          </div>
        </div>
      ) : null}

      {roundEditable ? (
        <p className="text-sm text-muted-foreground">
          Drag players between slots, assign empty positions, record scores, or remove someone from
          the round. Winners advance automatically in single elimination.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          This round is view only. Upcoming rounds can be edited before they start.
        </p>
      )}

      <PlayEventDrawMatchList
        event={event}
        organizationId={organizationId}
        eventId={event.id}
        matches={matchListItems}
        participantsById={participantsById}
        teamsById={teamsById}
        courtNamesById={resolvedCourtNamesById}
        ratingSettings={ratingSettings}
        editable={roundEditable}
        draggingParticipantId={draggingParticipantId}
        sittingOutParticipants={sittingOut}
        unevenPanelTitle="Sitting out"
        onParticipantUpdated={onParticipantUpdated}
        onCourtChange={(matchId, courtId) => void handleCourtChange(matchId, courtId)}
        onRemoveParticipant={(participantId) => void handleRemoveParticipant(participantId)}
        assigningCourtMatchId={assigningCourtMatchId}
        availableParticipantsForSlots={roundEditable ? availableParticipantOptions : []}
        onRecordScore={(matchId, data) => void handleRecordScore(matchId, data)}
        scoringMatchId={scoringMatchId}
        participantsLoading={participantsLoading}
        onAssignParticipantToSlot={
          roundEditable
            ? (participantId, matchKey, side) => {
                void (async () => {
                  if (selectedRoundNumber == null || swapping) return;
                  setSwapping(true);
                  try {
                    const res = await playEventService.assignRoundParticipant(
                      organizationId,
                      event.id,
                      selectedRoundNumber,
                      {
                        participantId,
                        targetMatchId: matchKey,
                        targetSide: side,
                      }
                    );
                    if (res.success) {
                      toast.success('Player assigned');
                      onParticipantUpdated?.();
                    } else {
                      toast.error(res.error ?? 'Failed to assign player');
                    }
                  } finally {
                    setSwapping(false);
                  }
                })();
              }
            : undefined
        }
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(92vh,52rem)] w-[min(96vw,72rem)] max-w-none flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
          <DialogTitle>Match draw</DialogTitle>
          <DialogDescription>
            Review rounds, record scores, edit upcoming matchups, or view the elimination bracket.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <aside className="hidden w-52 shrink-0 overflow-y-auto border-r border-border bg-muted/20 p-3 sm:block">
            <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Rounds
            </p>
            <nav className="space-y-1" aria-label="Round list">
              {roundNumbers.map((roundNumber) => {
                const round = roundsByNumber.get(roundNumber);
                const statusLabel = getPlayEventRoundDrawStatusLabel(
                  round,
                  matches,
                  roundNumber,
                  event
                );
                const isSelected = selectedRoundNumber === roundNumber;
                return (
                  <button
                    key={roundNumber}
                    type="button"
                    onClick={() => {
                      setSelectedRoundNumber(roundNumber);
                      setViewTab('round');
                    }}
                    className={cn(
                      'flex w-full flex-col rounded-lg px-3 py-2 text-left text-sm transition-colors',
                      isSelected && viewTab === 'round'
                        ? 'bg-background font-medium text-foreground shadow-sm ring-1 ring-border'
                        : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
                    )}
                  >
                    <span>Round {roundNumber}</span>
                    <span className="text-xs font-normal">{statusLabel}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
            {event.format === 'single_elimination' ? (
              <div className="mb-4 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={viewTab === 'round' ? 'default' : 'outline'}
                  onClick={() => setViewTab('round')}
                >
                  Round view
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewTab === 'bracket' ? 'default' : 'outline'}
                  onClick={() => setViewTab('bracket')}
                >
                  Bracket
                </Button>
              </div>
            ) : null}

            {roundNumbers.length > 1 && viewTab === 'round' ? (
              <div className="mb-4 flex gap-2 overflow-x-auto sm:hidden">
                {roundNumbers.map((roundNumber) => (
                  <button
                    key={roundNumber}
                    type="button"
                    onClick={() => setSelectedRoundNumber(roundNumber)}
                    className={cn(
                      'shrink-0 rounded-full border px-3 py-1 text-sm',
                      selectedRoundNumber === roundNumber
                        ? 'border-primary-500 bg-primary-600 text-white'
                        : 'border-border bg-background text-muted-foreground'
                    )}
                  >
                    R{roundNumber}
                  </button>
                ))}
              </div>
            ) : null}

            {viewTab === 'bracket' && event.format === 'single_elimination' ? (
              <PlayEventBracketTree
                matches={bracketPreviewMatches}
                participantNamesById={participantNamesById}
              />
            ) : selectedRoundNumber == null ? (
              <p className="text-sm text-muted-foreground">No rounds to display.</p>
            ) : roundEditable ? (
              <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
                onDragEnd={(dragEvent) => void handleDragEnd(dragEvent)}
              >
                {roundContent}
                <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
                  {draggingParticipant ? (
                    <PlayEventDrawPlayerDragGhost
                      participant={draggingParticipant}
                      event={event}
                      ratingSettings={ratingSettings}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            ) : (
              roundContent
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
